/**
 * Settings -> Notifications -> WhatsApp: a company's OWN WhatsApp connection through Mehery (v1 supports
 * Mehery only), which of its approved templates each notification uses, and who has opted in.
 *
 * Nothing is sent on WhatsApp for a company until ALL of these hold: the connection is enabled and
 * verified, the notification is mapped to an approved template, the person has a recorded opt-in, and
 * the channel is switched on in the Rules screen. WhatsApp stays OFF by default.
 *
 * THE API ID AND KEY ARE WRITE-ONLY. They are accepted on `PUT`, encrypted server-side, and never appear
 * in any response, log or audit diff — responses carry only `credentialsSet`. Omit them on a later `PUT`
 * to keep the stored ones.
 *
 * ENABLING IS GATED ON A TEST. Changing the base URL, credentials or channel clears `verifiedAt` and
 * switches the connection off; it can only be switched on again after `POST /test` succeeds against the
 * current saved settings. A test message goes only to the phone number recorded for the signed-in caller.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { routeMeta } from './meta.js';
import { errorBodySchema, instantSchema, orgIdSchema } from '../schemas/common.js';

const c = initContract();

/**
 * Who may receive WhatsApp. `explicit` (default): only people with a recorded opt-in. `all_employees`:
 * every employee with a phone number, EXCEPT anyone recorded as opted out — the company confirms it
 * already holds their consent (e.g. through employment terms). An opt-out always wins in both modes.
 */
export const WHATSAPP_CONSENT_MODES = ['explicit', 'all_employees'] as const;
export type WhatsappConsentMode = (typeof WHATSAPP_CONSENT_MODES)[number];

/** Provider hosts a company base URL must end with (v1: Mehery only). Checked again at send time. */
export const WHATSAPP_ALLOWED_HOST_SUFFIXES = ['.mehery.com', '.mehery.io'] as const;

const digitsPhoneSchema = z
  .string()
  .trim()
  .regex(/^\d{8,15}$/, 'Digits only, with country code and no plus (e.g. 919876543210)');

export const orgWhatsappSettingsSchema = z.object({
  orgId: orgIdSchema,
  /** Whether the connection is in use. Only ever true after a successful test. */
  enabled: z.boolean(),
  provider: z.literal('mehery'),
  baseUrl: z.string().nullable(),
  channelId: z.string().nullable(),
  defaultLanguage: z.string(),
  consentMode: z.enum(WHATSAPP_CONSENT_MODES),
  /** True once the API Id and Key are stored. They are never returned. */
  credentialsSet: z.boolean(),
  verifiedAt: instantSchema.nullable(),
  lastTestAt: instantSchema.nullable(),
  lastTestStatus: z.enum(['success', 'failed']).nullable(),
  lastTestError: z.string().nullable(),
  updatedAt: instantSchema.nullable(),
});
export type OrgWhatsappSettings = z.infer<typeof orgWhatsappSettingsSchema>;

export const updateOrgWhatsappSettingsBodySchema = z.object({
  /** https://<company>.mehery.com/xms — https only, Mehery host only. */
  baseUrl: z.string().trim().url().max(300),
  /** Write-only. Omit both to keep the stored credentials; required the first time settings are saved. */
  apiId: z.string().trim().min(1).max(200).optional(),
  apiKey: z.string().trim().min(1).max(500).optional(),
  channelId: digitsPhoneSchema,
  defaultLanguage: z
    .string()
    .trim()
    .regex(/^[a-z]{2}(_[A-Z]{2})?$/, 'e.g. en or en_US')
    .default('en_US'),
  consentMode: z.enum(WHATSAPP_CONSENT_MODES).default('explicit'),
  /** Rejected (400) unless the current settings have passed a test. */
  enabled: z.boolean(),
});
export type UpdateOrgWhatsappSettingsBody = z.infer<typeof updateOrgWhatsappSettingsBodySchema>;

export const orgWhatsappTestResultSchema = z.object({
  status: z.enum(['success', 'failed']),
  /** A safe, human-readable reason on failure — never a raw provider response, never a credential. */
  error: z.string().nullable(),
  testedAt: instantSchema,
  /** The number the test went to, masked (the caller's own recorded number). */
  sentTo: z.string(),
});
export type OrgWhatsappTestResult = z.infer<typeof orgWhatsappTestResultSchema>;

// ---- templates (read-only) ----------------------------------------------------------------------

/**
 * The templates a company must create in ITS Mehery account. Defined once by HRMS (see
 * `WHATSAPP_TEMPLATES`), so there is nothing to configure here — the screen shows what to create.
 */
export const whatsappTemplateGuideSchema = z.object({
  eventKey: z.string(),
  label: z.string(),
  /** The exact template name to create in Mehery. */
  code: z.string(),
  variables: z.array(z.object({ name: z.string(), description: z.string() })),
  /** Suggested message text to paste into Mehery (variables written as `{{data.<name>}}`). */
  sampleText: z.string(),
});
export type WhatsappTemplateGuide = z.infer<typeof whatsappTemplateGuideSchema>;

export const listWhatsappTemplatesResponseSchema = z.object({
  /** Language the templates must be approved in, from the connection settings. */
  language: z.string(),
  templates: z.array(whatsappTemplateGuideSchema),
});
export type ListWhatsappTemplatesResponse = z.infer<typeof listWhatsappTemplatesResponseSchema>;

// ---- consent ------------------------------------------------------------------------------------

export const whatsappConsentSchema = z.object({
  personId: z.string().uuid(),
  personName: z.string(),
  /** Masked (last four digits) — the full number is used only when sending. */
  phoneMasked: z.string(),
  optedInAt: instantSchema.nullable(),
  optedOutAt: instantSchema.nullable(),
  source: z.enum(['hr_admin', 'self', 'import']),
});
export type WhatsappConsent = z.infer<typeof whatsappConsentSchema>;

export const listWhatsappConsentsQuerySchema = z.object({
  search: z.string().trim().max(100).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
});

export const listWhatsappConsentsResponseSchema = z.object({
  items: z.array(whatsappConsentSchema),
  total: z.number().int(),
});
export type ListWhatsappConsentsResponse = z.infer<typeof listWhatsappConsentsResponseSchema>;

export const updateWhatsappConsentBodySchema = z.object({
  personId: z.string().uuid(),
  /** true records an opt-in (needs `phone`); false records an opt-out, which always wins. */
  optedIn: z.boolean(),
  /** Required when opting in. Spaces, dashes and a leading plus are accepted and normalised to digits. */
  phone: z.string().trim().max(30).optional(),
});
export type UpdateWhatsappConsentBody = z.infer<typeof updateWhatsappConsentBodySchema>;

export const whatsappSettingsContract = c.router(
  {
    get: {
      method: 'GET',
      path: '/',
      responses: { 200: orgWhatsappSettingsSchema, 401: errorBodySchema, 403: errorBodySchema },
      summary: "The company's WhatsApp (Mehery) connection (credentials never returned)",
      metadata: routeMeta(['notification.config:read', 'notification.config:manage']),
    },
    update: {
      method: 'PUT',
      path: '/',
      body: updateOrgWhatsappSettingsBodySchema,
      responses: {
        200: orgWhatsappSettingsSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        /** The base URL is not a Mehery https address. */
        422: errorBodySchema,
      },
      summary:
        'Save the WhatsApp connection; enabling requires a successful test of these settings',
      metadata: routeMeta('notification.config:manage'),
    },
    test: {
      method: 'POST',
      path: '/test',
      body: z.object({}).strict(),
      responses: {
        200: orgWhatsappTestResultSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        422: errorBodySchema,
        429: errorBodySchema,
      },
      summary:
        "Send a test WhatsApp message through the saved settings, to the caller's own opted-in number only",
      metadata: routeMeta('notification.config:manage'),
    },
    listTemplates: {
      method: 'GET',
      path: '/templates',
      responses: {
        200: listWhatsappTemplatesResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'The WhatsApp templates to create in the company Mehery account',
      metadata: routeMeta(['notification.config:read', 'notification.config:manage']),
    },
    listConsents: {
      method: 'GET',
      path: '/consents',
      query: listWhatsappConsentsQuerySchema,
      responses: {
        200: listWhatsappConsentsResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'People with a recorded WhatsApp opt-in or opt-out',
      metadata: routeMeta(['notification.config:read', 'notification.config:manage']),
    },
    updateConsent: {
      method: 'PUT',
      path: '/consents',
      body: updateWhatsappConsentBodySchema,
      responses: {
        200: whatsappConsentSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary: "Record a person's WhatsApp opt-in or opt-out",
      metadata: routeMeta('notification.config:manage'),
    },
  },
  { pathPrefix: '/notification-settings/whatsapp' },
);
